import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export const PageShell = ({ children, testid }) => (
  <motion.main
    data-testid={testid}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease: EASE }}
  >
    {children}
  </motion.main>
);

export const FadeUp = ({ children, delay = 0, className = "", ...rest }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    let done = false;
    const show = () => {
      if (!done) {
        done = true;
        setInView(true);
      }
    };
    const check = () => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight - 60 && r.bottom > 0) show();
    };
    let io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              show();
              if (io) io.disconnect();
            }
          });
        },
        { rootMargin: "0px 0px -60px 0px" }
      );
      io.observe(el);
    }
    // Fallbacks: IntersectionObserver can fail to fire right after a client-side
    // route transition, leaving the page invisible until refresh. Re-check on a
    // timer and on scroll so in-viewport content always becomes visible.
    const t1 = setTimeout(check, 350);
    const t2 = setTimeout(check, 1200);
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      if (io) io.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [inView]);

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const MaskedLine = ({ children, delay = 0, className = "", as: Tag = "span" }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Eyebrow = ({ children, className = "" }) => (
  <p className={`text-xs uppercase tracking-[0.3em] font-medium text-bronze ${className}`}>
    {children}
  </p>
);

export const SectionHeading = ({ eyebrow, title, className = "" }) => (
  <FadeUp className={className}>
    <Eyebrow className="mb-5">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-snug text-bone">
      {title}
    </h2>
  </FadeUp>
);
